import { action, computed } from 'mobx'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import type { ActivationLifecycle } from '@/infra/activation'
import type { SubmittedJob } from '@/features/jobs/details/SubmittedJob'
import type { TaxJobType } from '@/features/jobs/job-type'
import type { ProgressionViewModel } from '@/features/jobs/details/tax-type/ProgressionViewModel'
import type ConsultationManagerViewModel from '@/features/jobs/details/consultation/ConsultationManagerViewModel'
import type { TaxJobConversationViewModel } from '@/features/jobs/details/tax-type/TaxJobConversationViewModel'
import type { TaxInfoGatheringViewModel } from '@/features/jobs/details/tax-type/info-gathering/TaxInfoGatheringViewModel'
import { ModalViewModel } from '@/infra/ModalViewModel'
import type { Section7216AcceptanceViewModel } from './section-7216/Section7216AcceptanceViewModel'
import { withAbortSignal } from '@/utils/rx-util'
import { defer, filter, share, switchMap, type Observable } from 'rxjs'
import type { EnsureTermsAcceptanceResult } from '@/features/account'
import type { TxfJobViewModel } from './txf/TxfJobViewModel'

/**
 * Tax job view model.
 */
export class TaxJobDetailsViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  private readonly _termsOutcome$: Observable<EnsureTermsAcceptanceResult>

  /**
   * View model for managing the open-items dialog.
   */
  readonly openItemsDialog = new ModalViewModel()

  /**
   * Observable that emits when the user should be bounced from the tax job details.
   * This happens when they decline Section 7216 (but only after terms are accepted).
   * Uses defer to handle the circular dependency with the parent VM.
   */
  readonly bounce$: Observable<unknown> = defer(() => this._termsOutcome$).pipe(
    filter((outcome) => outcome !== 'Declined'),
    switchMap(() =>
      withAbortSignal(async (signal) =>
        this.section7216AcceptanceVm.ensureAcceptance(signal),
      ).pipe(filter((outcome) => outcome === 'Declined')),
    ),
    share(),
  )

  constructor(
    public readonly job: SubmittedJob,
    public readonly progressionVm: ProgressionViewModel,
    public readonly consultationManagerVm: ConsultationManagerViewModel,
    public readonly jobConversationVm: TaxJobConversationViewModel,
    public readonly infoGatheringVm: TaxInfoGatheringViewModel,
    public readonly section7216AcceptanceVm: Section7216AcceptanceViewModel,
    public readonly txfJobVm: TxfJobViewModel,
    termsOutcome$: Observable<EnsureTermsAcceptanceResult>,
  ) {
    this._termsOutcome$ = termsOutcome$
    this._lifecycle.manage(this.progressionVm)
    this._lifecycle.manage(this.consultationManagerVm)
    this._lifecycle.manage(this.infoGatheringVm)
    this._lifecycle.manage(this.section7216AcceptanceVm)
    this._lifecycle.manage(this.txfJobVm)
    this._lifecycle.manage(this.bounce$)
  }

  @computed
  get type() {
    // We know this is safe.
    return this.job.type as TaxJobType
  }

  @computed
  get isExtensionOnlyJob() {
    return this.type.isExtensionOnlyJob
  }

  @computed
  get hasTxfIntegration() {
    return Boolean(this.txfJobVm.txfJob)
  }

  /**
   * Fetches data needed for the view to be able to render.
   */
  async fetch(signal?: AbortSignal) {
    await Promise.all([
      this.infoGatheringVm.fetchChecklist(signal),
      this.txfJobVm.fetchTxfJob(signal),
    ])
  }

  /**
   * Shows the open-items dialog.
   */
  @action.bound
  showOpenItemsDialog() {
    this.openItemsDialog.show()
  }

  /**
   * Closes the open-items dialog.
   */
  @action.bound
  closeOpenItemsDialog() {
    this.openItemsDialog.close()
  }

  activate() {
    this._lifecycle.activate()
  }

  deactivate() {
    this._lifecycle.deactivate()
    this.openItemsDialog.close()
  }
}
